// Connected container — fetches indexed chunks (`useCaseDocumentInternal`) and
// renders the entity / relationship accordions (which fetch on demand). Not
// storyable (needs QueryClient + Router + overlay context). Composes the pure
// leaf pieces (`MetaField`, `DetailSection`, `EmptyHint`, `ChunkCard`).

import {
  AlertCircle,
  AlignLeft,
  Car,
  ChevronRight,
  ExternalLink,
  MapPin,
  ScanText,
  Sparkles,
  Users,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { RoutingReasoning } from '@/components/extraction/RoutingReasoning'
import { MergeIdentitiesDialog } from '@/components/faces/MergeIdentitiesDialog'
import {
  type CreateIdentityDraft,
  CreateIdentityFromFacesDialog,
  EMPTY_IDENTITY_DRAFT,
  identityNamesFromDraft,
} from '@/components/identities/CreateIdentityFromFacesDialog'
import type { DocumentFocus } from '@/features/document-overlay/focus'
import {
  formatBytes,
  formatDate,
  formatPageList,
  formatTime,
  importScopeTitle,
} from '@/features/document-overlay/format'
import {
  chunksFromInternalNodes,
  groupEntitiesByLabel,
  groupRelationshipsByType,
} from '@/features/document-overlay/grouping'
import type { UseDocumentFaceSectionResult } from '@/features/document-overlay/hooks/useDocumentFaces'
import type { UseDocumentPlatesResult } from '@/features/document-overlay/hooks/useDocumentPlates'
import type { UseDocumentVideoResult } from '@/features/document-overlay/hooks/useDocumentVideo'
import { useReceivedDateEdit } from '@/features/documents/hooks/useReceivedDateEdit'
import { identityPath } from '@/features/identities/identity-path'
import { DocumentStructuredData } from '@/features/structured-ingest/DocumentStructuredData'
import { useCaseDocumentInternal } from '@/hooks/use-case-queries'
import type { CaseDocumentDetail } from '@/services/api/cases'
import { ChunkCard } from './ChunkCard'
import { DetailSection } from './DetailSection'
import { EmptyHint } from './EmptyHint'
import { EntityAccordion } from './EntityAccordion'
import { FacesOnPhoto } from './FacesOnPhoto'
import { MetaField } from './MetaField'
import { PlatesInRecording } from './PlatesInRecording'
import { ReceivedDateField } from './ReceivedDateField'
import { RelationshipAccordion } from './RelationshipAccordion'

export function DetailRailBody({
  caseId,
  file,
  focus,
  faces,
  video,
  plates,
  compact = false,
}: {
  caseId: string
  file: CaseDocumentDetail
  /**
   * What the overlay was opened on. Its identity turns the focused face's row
   * into the full comparison of slice 08; an empty focus leaves every row as it
   * was in 06.
   */
  focus: DocumentFocus
  /**
   * The document's faces, their shared selection and their decisions. The overlay
   * reads them once and hands the same object to the preview, so a row and its
   * box can never point at different faces (slice 06).
   */
  faces: UseDocumentFaceSectionResult
  /**
   * The recording's player state (PRD-0074): its rows get "Play pass" and the section is
   * named for a recording. Omitted for a document that is no video.
   */
  video?: UseDocumentVideoResult
  /** The plates read in the recording (slice 07); omitted for a document that is no video. */
  plates?: UseDocumentPlatesResult
  compact?: boolean
}) {
  const { t } = useTranslation()
  // The create dialog's own draft: it starts empty every time a face opens it.
  const [draft, setDraft] = useState<CreateIdentityDraft>(EMPTY_IDENTITY_DRAFT)
  useEffect(() => {
    if (faces.createForFaceId !== null) setDraft(EMPTY_IDENTITY_DRAFT)
  }, [faces.createForFaceId])
  // An unexplained failure keeps its own sentence out of the server's hands.
  const createRefusal =
    faces.createError === null ? null : faces.createError || t('identity.unassigned.writeFailed')
  const submitCreate = () => faces.createIdentity(identityNamesFromDraft(draft))
  const groupedEntities = useMemo(
    () => groupEntitiesByLabel(file.entities, file.entity_label_counts),
    [file.entities, file.entity_label_counts]
  )
  const groupedRels = useMemo(
    () => groupRelationshipsByType(file.relationships, file.relationship_type_counts),
    [file.relationships, file.relationship_type_counts]
  )
  const totalEntities = file.entity_count ?? file.entities.length
  const totalRels = file.relationship_count ?? file.relationships.length
  const trackTier = file.geo_capability?.tier
  const showTrack = trackTier && trackTier !== 'none'
  // Only the 'animated' tier has a track (and a time range) to offer; 'static'
  // is a set of pins, so it must not borrow the animated copy.
  const isAnimated = trackTier === 'animated'

  // Pull chunks from the internal-provenance endpoint so analysts can verify
  // *something* was extracted even when entities = 0 (common for audio
  // transcripts that yield no graph entities). Fetch only after processing
  // completes; the endpoint short-circuits before Neo4j is hit otherwise.
  const internalQuery = useCaseDocumentInternal(caseId, file.id, file.status === 'processed')
  // A collaborator changes the received date in place (ADR-0094); a viewer and a capture only read it.
  const receivedDateEdit = useReceivedDateEdit(caseId, file)
  const chunks = useMemo(
    () => chunksFromInternalNodes(internalQuery.data?.nodes ?? []),
    [internalQuery.data]
  )

  return (
    <div
      style={{
        padding: compact ? '18px 16px 28px' : '22px 22px 28px',
        display: 'flex',
        flexDirection: 'column',
        gap: 22,
      }}
    >
      <div
        style={{
          display: 'grid',
          // Stacked (phone): two equal columns that may shrink, so one long value
          // wraps instead of pushing its neighbour off the row.
          gridTemplateColumns: compact ? 'minmax(0, 1fr) minmax(0, 1fr)' : '1fr 1fr',
          gap: 18,
        }}
      >
        <MetaField
          label={t('documents.detail.type')}
          value={(file.file_type ?? '—').toUpperCase()}
        />
        <MetaField label={t('documents.detail.size')} value={formatBytes(file.file_size)} />
        <ReceivedDateField receivedAt={file.received_at} edit={receivedDateEdit} />
        <MetaField
          label={t('documents.detail.uploaded')}
          value={formatDate(file.created_at)}
          sub={formatTime(file.created_at)}
        />
        <MetaField
          label={t('documents.detail.processed')}
          value={file.processed_at ? formatDate(file.processed_at) : '—'}
          sub={file.processed_at ? formatTime(file.processed_at) : undefined}
        />
        <MetaField
          label={t('documents.detail.extractionSchema')}
          value={
            file.schema_name
              ? `${file.schema_name}${file.schema_version ? ` ${file.schema_version}` : ''}`
              : '—'
          }
          info={
            file.user_prompt ? (
              <div>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 1,
                    color: 'var(--muted-foreground)',
                    marginBottom: 6,
                  }}
                >
                  {t('documents.detail.additionalContext')}
                </div>
                <pre
                  style={{
                    margin: 0,
                    fontSize: 12,
                    lineHeight: 1.5,
                    color: 'var(--foreground)',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: 'inherit',
                  }}
                >
                  {file.user_prompt}
                </pre>
              </div>
            ) : undefined
          }
        />
        <MetaField
          label={t('documents.detail.selectedPipeline')}
          value={file.pipeline ?? '—'}
          info={file.routing_facts ? <RoutingReasoning facts={file.routing_facts} /> : undefined}
        />
      </div>

      {file.status === 'failed' && file.failure_reason && (
        <div
          style={{
            display: 'flex',
            gap: 10,
            padding: '12px 14px',
            borderRadius: '12px',
            background: 'var(--destructive-surface)',
            border: '1px solid var(--destructive)',
          }}
        >
          <AlertCircle
            size={16}
            style={{ color: 'var(--destructive)', flexShrink: 0, marginTop: 1 }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: 1,
                color: 'var(--destructive)',
                marginBottom: 4,
              }}
            >
              {t('documents.detail.failureReason')}
            </div>
            <pre
              style={{
                margin: 0,
                fontSize: 12,
                lineHeight: 1.5,
                color: 'var(--destructive)',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: 'inherit',
              }}
            >
              {t(`documents.detail.failureReasons.${file.failure_reason}`, {
                defaultValue: t('documents.detail.failureReasons.processing_failed'),
              })}
            </pre>
          </div>
        </div>
      )}

      {file.import_scope && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '12px 14px',
            borderRadius: '12px',
            background: 'var(--primary-surface)',
            border: '1px solid var(--primary)',
          }}
        >
          <span
            style={{
              width: 28,
              height: 28,
              flexShrink: 0,
              borderRadius: '8px',
              background: 'var(--accent)',
              border: '1px solid var(--border)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--primary)',
            }}
          >
            <Sparkles size={14} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: 'var(--primary)',
                marginBottom: 2,
              }}
            >
              {importScopeTitle(file.import_scope)}
            </div>
            {file.import_scope.scope_type === 'pages' &&
            file.import_scope.selected_pages?.length ? (
              <div
                style={{
                  fontSize: 11.5,
                  color: 'var(--muted-foreground)',
                  fontFamily: 'var(--font-mono)',
                  letterSpacing: 0.2,
                  wordBreak: 'break-word',
                }}
              >
                Pages: {formatPageList(file.import_scope.selected_pages)}
              </div>
            ) : null}
            {file.import_scope.scope_type === 'tabular' &&
            file.import_scope.selected_columns?.length ? (
              <div
                style={{
                  fontSize: 11.5,
                  color: 'var(--muted-foreground)',
                  fontFamily: 'var(--font-mono)',
                  letterSpacing: 0.2,
                  wordBreak: 'break-word',
                }}
              >
                Columns: {file.import_scope.selected_columns.join(', ')}
              </div>
            ) : null}
            {file.import_scope.scope_note ? (
              <div
                style={{
                  fontSize: 11.5,
                  color: 'var(--muted-foreground)',
                  marginTop: 2,
                }}
              >
                {file.import_scope.scope_note}
              </div>
            ) : null}
            <Link
              to={`/data?doc=${file.import_scope.lake_document_id}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                marginTop: 6,
                fontSize: 11.5,
                fontWeight: 500,
                color: 'var(--primary)',
                textDecoration: 'none',
              }}
            >
              <ExternalLink size={11} />
              View original in Data Lake
            </Link>
          </div>
        </div>
      )}

      {/* Images: the VLM description written by the `describe_image` step. Only
          images carry one, so its presence is the whole condition. */}
      {file.description ? (
        <div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 12,
              padding: '0 2px',
            }}
          >
            <ScanText size={13} style={{ color: 'var(--primary)' }} />
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--foreground)' }}>
              {t('documents.detail.description')}
            </span>
          </div>
          <p
            style={{
              margin: 0,
              fontSize: 12.5,
              lineHeight: 1.6,
              color: 'var(--muted-foreground)',
              whiteSpace: 'pre-wrap',
            }}
          >
            {file.description}
          </p>
        </div>
      ) : null}

      {/* Tabular files: what structured ingest made of them (renders nothing otherwise). */}
      <DocumentStructuredData caseId={caseId} document={file} />

      {showTrack && file.geo_capability && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '12px 14px',
            borderRadius: '12px',
            background: 'var(--primary-surface)',
            border: '1px solid var(--primary)',
          }}
        >
          <span
            style={{
              width: 28,
              height: 28,
              flexShrink: 0,
              borderRadius: '8px',
              background: 'var(--accent)',
              border: '1px solid var(--border)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--primary)',
            }}
          >
            <MapPin size={14} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: 'var(--primary)',
                marginBottom: 2,
              }}
            >
              {isAnimated
                ? t('documents.detail.animatedTrackAvailable')
                : t('documents.detail.staticLocationsAvailable')}
            </div>
            <div
              style={{
                fontSize: 11.5,
                color: 'var(--muted-foreground)',
                fontFamily: 'var(--font-mono)',
                letterSpacing: 0.2,
              }}
            >
              {isAnimated ? (
                <>
                  {t('documents.detail.entityCount', {
                    count: file.geo_capability.animated_entity_count,
                  })}{' '}
                  · {file.geo_capability.time_range_start ?? '—'} –{' '}
                  {file.geo_capability.time_range_end ?? '—'}
                </>
              ) : (
                t('documents.detail.locationCount', {
                  count: file.geo_capability.static_location_count,
                })
              )}
            </div>
          </div>
          <ChevronRight size={14} style={{ color: 'var(--primary)', flexShrink: 0 }} />
        </div>
      )}

      {/* Every face found on this photo, whatever the analyst has decided about
          it (slice 06). A document with no faces at all keeps the section hidden. */}
      {faces.section.rows.length > 0 || faces.section.collapsed.length > 0 ? (
        <DetailSection
          icon={<Users size={13} style={{ color: 'var(--primary)' }} />}
          label={t(
            video?.playback
              ? 'documents.detail.faces.sectionVideo'
              : 'documents.detail.faces.section'
          )}
          count={faces.section.allFaces.length}
        >
          <FacesOnPhoto
            rows={faces.section.rows}
            collapsed={faces.section.collapsed}
            selectedFaceId={faces.selectedFaceId}
            focusPersonId={focus.personId}
            pendingFaceId={faces.pendingFaceId}
            errorKey={faces.errorKey}
            identityHref={personId => identityPath(caseId, personId)}
            onSelect={faces.select}
            actions={faces.actions}
            onPlayPass={video?.playback?.playback_url ? video.playPass : undefined}
          />
        </DetailSection>
      ) : null}

      {/* Every plate read in the recording, with its vehicle or the decision it waits for (slice 07). */}
      {plates && plates.plates.length > 0 ? (
        <DetailSection
          icon={<Car size={13} style={{ color: 'var(--primary)' }} />}
          label={t('documents.detail.plates.section')}
          count={plates.plates.length}
        >
          <PlatesInRecording
            plates={plates.plates}
            actions={plates.actions}
            onPlaySpan={video?.playback?.playback_url ? video.playSpan : undefined}
          />
        </DetailSection>
      ) : null}

      {/* The star and the candidate table ask with the very same dialog (ADR-0042). */}
      <MergeIdentitiesDialog
        pair={faces.merge.pair}
        onConfirm={faces.merge.confirm}
        onClose={faces.merge.close}
      />
      {/* One dialog for both entry points — the tab opens the very same one. */}
      <CreateIdentityFromFacesDialog
        open={faces.createForFaceId !== null}
        onOpenChange={open => {
          if (!open) faces.closeCreate()
        }}
        faceCount={faces.createForFaceId === null ? 0 : 1}
        draft={draft}
        onDraftChange={setDraft}
        onSubmit={() => void submitCreate()}
        isSubmitting={faces.isCreating}
        error={createRefusal}
      />
      <DetailSection
        icon={<Sparkles size={13} style={{ color: 'var(--primary)' }} />}
        label={t('documents.detail.extractedEntities')}
        count={totalEntities}
      >
        {groupedEntities.length === 0 ? (
          <EmptyHint>{t('documents.detail.noEntitiesYet')}</EmptyHint>
        ) : (
          groupedEntities.map((group, idx) => (
            <EntityAccordion
              key={group.label}
              caseId={caseId}
              documentId={file.id}
              group={group}
              defaultOpen={idx === 0}
            />
          ))
        )}
      </DetailSection>

      <DetailSection
        icon={<Sparkles size={13} style={{ color: 'var(--primary)' }} />}
        label={t('documents.detail.extractedRelationships')}
        count={totalRels}
      >
        {groupedRels.length === 0 ? (
          <EmptyHint>{t('documents.detail.noRelationshipsYet')}</EmptyHint>
        ) : (
          groupedRels.map(group => (
            <RelationshipAccordion key={group.type} caseId={caseId} group={group} />
          ))
        )}
      </DetailSection>

      {file.status === 'processed' && (
        <DetailSection
          icon={<AlignLeft size={13} style={{ color: 'var(--primary)' }} />}
          label={t('documents.detail.indexedContent', 'Indexed Content')}
          count={chunks.length}
        >
          {internalQuery.isLoading && chunks.length === 0 ? (
            <EmptyHint>Loading…</EmptyHint>
          ) : chunks.length === 0 ? (
            <EmptyHint>
              {t('documents.detail.noChunksYet', 'No indexed chunks for this document.')}
            </EmptyHint>
          ) : (
            chunks.map(chunk => <ChunkCard key={chunk.id} chunk={chunk} />)
          )}
        </DetailSection>
      )}
    </div>
  )
}
